/**
 * The address an account signs in with, composed from a username and a family
 * inbox.
 *
 * Supabase Auth requires a globally unique address per account. A family has
 * one inbox. Those two facts are irreconcilable until you notice that the
 * plus-address a mail provider already routes to the same mailbox is, to
 * Supabase, a different address entirely:
 *
 *     lea + papa@x.be   ->   papa+lea@x.be
 *
 * So Léa gets her own account, her own session and her own member row, while
 * the code that lets her in arrives in her parent's inbox. This is
 * `docs/architecture-review.md` §3's second option — the one it said "works
 * today" — and it is what makes the third option (a child with a private login)
 * possible without an Edge Function that mints sessions.
 *
 * **It depends on the mail provider accepting `+`.** Gmail, Outlook, Fastmail
 * and every provider a household is likely to use do; a few corporate relays
 * strip or reject it. A member whose invitation never arrives needs an address
 * of their own, which is the same form with the username left blank.
 *
 * Composition lives here and nowhere else, because two places compose it and
 * they must agree exactly: the invitation is sent to whatever the owner's form
 * produced, and months later the login screen has to reproduce it from what the
 * member types. A second implementation that differed by a lowercase would send
 * a code to an address nobody can sign in as.
 */

/**
 * What a username may contain.
 *
 * Lowercase and digits with internal hyphens, 2–20 characters. Three of the
 * exclusions are not cosmetic:
 *
 *   - `+` would compose a second sub-address inside the first;
 *   - `@` would compose something that is not an address at all;
 *   - `:` is the completion-id separator, and while a username is not an id, a
 *     rule that lets one through here invites somebody to derive the other from
 *     it later.
 */
const USERNAME = /^[a-z0-9][a-z0-9-]{1,19}$/

export function normalizeUsername(value: string): string {
  return value.trim().toLowerCase()
}

export function isValidUsername(value: string): boolean {
  return USERNAME.test(normalizeUsername(value))
}

/** A shape check, not a verdict — the mail server is the only real judge. */
export function isPlausibleEmail(value: string): boolean {
  const trimmed = value.trim()
  return /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(trimmed)
}

/**
 * The address to send a code to, and the address to sign in as.
 *
 * An empty username returns the address unchanged — that is the owner's path,
 * and the path for a member who has an inbox of their own. Both halves are
 * lowercased because Postgres compares `auth_email` case-insensitively and
 * Supabase stores addresses folded; leaving the case alone here would mean the
 * member row and the auth user disagreed for anybody who capitalised their
 * address.
 *
 * Returns `''` when it cannot compose something usable, so the one caller that
 * needs to refuse (the member form) refuses on a falsy value rather than on a
 * thrown error inside an input handler.
 */
export function composeAuthEmail(email: string, username: string): string {
  const address = email.trim().toLowerCase()
  if (!isPlausibleEmail(address)) return ''

  const handle = normalizeUsername(username)
  if (!handle) return address
  if (!isValidUsername(handle)) return ''

  const at = address.lastIndexOf('@')
  const local = address.slice(0, at)
  const domain = address.slice(at + 1)

  // Already sub-addressed: `papa+lea@x.be` with username `lea` must stay
  // `papa+lea@x.be` and not become `papa+lea+lea@x.be`, or a member who typed
  // their real address into the login screen would be locked out of the account
  // that was made for them.
  if (local.endsWith(`+${handle}`)) return address

  return `${local}+${handle}@${domain}`
}
